import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Input from '../../common/Input'
import Typography from '../../common/Typography/index'
import Button from '../../common/Button/index'
import Select from '../../common/Select/index'
import { IoMdEye } from 'react-icons/io'
import { PiEyeSlashFill } from 'react-icons/pi'
import { createUser } from '../../../state/redux/actions/userActions'

const AddNewUserForm = ({ onSuccess, onCancel }) => {
  const dispatch = useDispatch()
  const { loading, error } = useSelector(state => state.user)

  const [value, setValue] = useState('Limited')
  const [showPassword, setShowPassword] = useState(false)
  const [formError, setFormError] = useState('')
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    confirm_password: '',
    type: 'admin',
    access_control: 'Limited',
  })

  const options = [
    { value: 'Limited', label: 'Limited' },
    { value: 'Full', label: 'Full' }
  ]

  const handleSelectChange = e => {
    const selectedValue = e.target.value
    setValue(selectedValue)
    setFormData(prev => ({
      ...prev,
      access_control: selectedValue
    }))
  }

  const handleInputChange = e => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const validateForm = () => {
    setFormError('')

    if (!formData.first_name.trim()) {
      setFormError('First name is required')
      return false
    }
    if (!formData.last_name.trim()) {
      setFormError('Last name is required')
      return false
    }
    if (!formData.email.trim()) {
      setFormError('Email is required')
      return false
    }
    if (!formData.password) {
      setFormError('Password is required')
      return false
    }
    if (formData.password !== formData.confirm_password) {
      setFormError('Passwords do not match')
      return false
    }
    return true
  }

  const handleSubmit = async e => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    try {
      const resultAction = await dispatch(createUser(formData))

      if (createUser.fulfilled.match(resultAction)) {
        // Success - reset form and call success callback
        setFormData({
          first_name: '',
          last_name: '',
          email: '',
          password: '',
          confirm_password: '',
          type: 'admin',
          access_control: 'Limited',
        })
        setValue('Limited')
        setFormError('')

        // Extract admin data from the API response
        const adminData = resultAction.payload?.data?.admin || resultAction.payload
        const apiPayload = resultAction.payload

        console.log('AddNewUserForm - Full resultAction:', resultAction)
        console.log('AddNewUserForm - API Payload:', apiPayload)
        console.log('AddNewUserForm - Form Data:', formData)

        // Call success callback with user data to close the form and add user to list
        // Use form data as primary source since API only returns success confirmation
        const userDataToPass = {
          firstName: formData.first_name,
          lastName: formData.last_name,
          email: formData.email,
          accessControl: formData.access_control,
          id: apiPayload?.adminId || Date.now(),
          type: formData.type || 'admin',
          verifiedEmail: false, // New users are not verified by default
          createdAt: new Date().toISOString(),
        }

        console.log('AddNewUserForm - userDataToPass:', userDataToPass)
        onSuccess && onSuccess(userDataToPass)

      } else {
        // Handle validation errors from API
        const errorData = resultAction.payload
        if (errorData?.validationErrors && Array.isArray(errorData.validationErrors)) {
          const errorMessages = errorData.validationErrors.map(err => err.message).join(', ')
          setFormError(errorMessages)
        } else {
          setFormError(errorData?.message || 'Failed to create admin user')
        }
      }

    } catch (error) {
      console.error('Error creating user:', error)
      setFormError('An unexpected error occurred. Please try again.')
    }
  }

  return (
    <div className="bg-white flex my-4 p-4 w-[600px] rounded-md justify-center items-center">
      <form autoComplete="off" className="p-4 w-full" onSubmit={handleSubmit}>
        <Typography align="center" variant="h3" className="py-4">
          Add New User
        </Typography>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          <div className="col-span-6">
            <label htmlFor="first_name">First Name</label>
            <Input
              id="first_name"
              type="text"
              name="first_name"
              value={formData.first_name}
              onChange={handleInputChange}
              placeholder="Enter first name"
              required
              className="rounded-md border-gray-300 px-3 py-2 w-full"
            />
          </div>
          <div className="col-span-6">
            <label htmlFor="last_name">Last Name</label>
            <Input
              id="last_name"
              type="text"
              name="last_name"
              value={formData.last_name}
              onChange={handleInputChange}
              placeholder="Enter last name"
              required
              className="rounded-md border-gray-300 px-3 py-2 w-full"
            />
          </div>
          <div className="col-span-6">
            <label htmlFor="email">Email</label>
            <Input
              id="email"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="Enter email address"
              required
              className="rounded-md border-gray-300 px-3 py-2 w-full"
            />
          </div>
          <div className="col-span-6">
            <label htmlFor="access_control">Access Control</label>
            <Select
              id="access_control"
              name="access_control"
              options={options}
              value={value}
              onChange={handleSelectChange}
              className=""
            />
          </div>
          <div className="col-span-6">
            <label htmlFor="password">Create Password</label>
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              icon={
                showPassword ? (
                  <IoMdEye onClick={() => setShowPassword(prev => !prev)} className="cursor-pointer" />
                ) : (
                  <PiEyeSlashFill onClick={() => setShowPassword(prev => !prev)} className="cursor-pointer" />
                )
              }
              placeholder="Enter password"
              required
              className="rounded-md border-gray-300 px-3 py-2 w-full"
            />
          </div>
          <div className="col-span-6">
            <label htmlFor="confirm_password">Re-enter Password</label>
            <Input
              id="confirm_password"
              type={showPassword ? 'text' : 'password'}
              name="confirm_password"
              value={formData.confirm_password}
              onChange={handleInputChange}
              icon={
                showPassword ? (
                  <IoMdEye onClick={() => setShowPassword(prev => !prev)} className="cursor-pointer" />
                ) : (
                  <PiEyeSlashFill onClick={() => setShowPassword(prev => !prev)} className="cursor-pointer" />
                )
              }
              placeholder="Re-enter password"
              required
              className="rounded-md border-gray-300 px-3 py-2 w-full"
            />
          </div>
          {(formError || error) && (
            <div className="col-span-12 text-red-600 text-sm bg-red-50 p-3 rounded-md">
              {formError || error}
            </div>
          )}
          <div className="flex justify-center items-center gap-4 col-span-12">
            <Button type="submit" disabled={loading} className="w-32 bg-teal-600 hover:bg-teal-700 text-white rounded-full">
              {loading ? 'Creating...' : 'Save'}
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={onCancel}
              disabled={loading}
              className="w-32"
            >
              Cancel
            </Button>
          </div>
        </div>
      </form>
    </div>
  )
}

export default AddNewUserForm